import * as React from 'react';
import _throttle from 'lodash/throttle';
import { useApolloClient } from 'react-apollo';

import { Container } from 'crehana-ui/GridV2';
import Text from 'crehana-ui/Text';
import { secondaryMain } from 'crehana-ui/styles/colors';
import PrimaryButton from 'crehana-ui/Buttons/PrimaryButton';
import Wand2 from 'crehana-icons/Wand2';

import { useTranslation } from 'Jsx/Shared/Hooks/useTranslation';
import Loadable from 'Jsx/Shared/Loadable';
import LoaderRing from 'Jsx/Shared/LoaderRing';
import EVENTS from 'Jsx/Shared/Tracking/events';
import { CourseProps } from 'Jsx/Shared/CourseCardV3/types';

import buyProduct, { formatProduct, trackProduct } from 'Jsx/Utils/buyProduct';
import mixpanelTracking from 'Jsx/Utils/mixpanelTracking';
import matchMediaQuery from 'Jsx/Utils/matchMediaQuery';

import { useCoursesInBundleState } from 'Jsx/ContextApis/CoursesInBundle';

import CROSS_SELL_QUERY from './graphql/crossSell.graphql';
import CoursePackItem from './CoursesInPackItem';
import { CUSTOM_EVENTS } from './types';

enum CoursePackPosition {
  TOP = 'top',
  BELOW_FILTERS = 'bottom',
}

type CoursesInBundleProps = {
  dockToBottom?: boolean;
  isCouponCampaign?: boolean;
  onClickBuyNow: (nextUrl: string, next?: (() => any) | undefined) => void;
};

const CoursesInBundle: React.FC<CoursesInBundleProps> = ({
  dockToBottom,
  isCouponCampaign,
  onClickBuyNow,
}) => {
  const {
    hasInitialized,
    coursesInBundle,
    toggleCourseInBundle,
    bundleInfo: {
      slug,
      size: bundleSize,
      formatedPrice: bundlePrice,
      ctaButtonText,
    },
    customBundle: {
      originalId,
      redirectUrl,
      isCompleted,
      loading: isBundleLoading,
    },
    cleanCustomBundle,
    addAllCoursesInCustomBundle,
  } = useCoursesInBundleState();

  const apolloClient = useApolloClient();
  const { t } = useTranslation();
  /* boolean to control the zIndex of the component itself
   see the first useEffect below for more info */
  const [isFilterBoxOpen, setIsFilterBoxOpen] = React.useState(false);
  const [coursePackPosition, setCoursePackPosition] = React.useState(
    CoursePackPosition.TOP,
  );

  const [showPackCourses, setShowpackCourses] = React.useState(false);
  const [isMobile, setMobile] = React.useState(false);
  const [courses, setCourses] = React.useState<CourseProps[]>([]);
  const [checkoutPopupState, setCheckoutPopupState] = React.useState({
    open: false,
    data: {
      itemId: NaN,
      itemType: '',
    },
  });
  const quantityCoursesinPack = coursesInBundle.length;
  const isCoursePackOnTop = coursePackPosition === CoursePackPosition.TOP;
  const defaultZIndex = isCoursePackOnTop ? 999 : 16;

  const completeRecommendedCourses = () => {
    const courseIds = coursesInBundle.map(course => course.originalId) || [];

    mixpanelTracking.track(EVENTS.CUSTOM_BUNDLE.RECOMMENDED_COURSES, { slug });
    apolloClient
      .query({
        query: CROSS_SELL_QUERY,
        variables: {
          itemsSelected: courseIds,
          pageSlug: slug,
        },
      })
      .then(({ data }) => {
        if (data?.crossSell?.edges.length > 0) {
          const recommendedIds =
            data.crossSell.edges.map(c => c.node.originalId) || [];
          const recommendedCourses =
            data.crossSell.edges.map(c => c.node) || [];

          let newIds: number[] = [];

          for (let i = 0; i < recommendedIds.length; i += 1) {
            if (!courseIds.includes(recommendedIds[i])) {
              newIds = [...newIds, recommendedIds[i]];
            }
          }

          addAllCoursesInCustomBundle(newIds, recommendedCourses);
        }
      });
  };

  // we need to `hide`(reduce the zIndex) this component if the Filter component is open...
  // ...because the Filter component shows belows this component. (z-index problem)
  // It's hard to fix this bug with css only because this component is a fixed div and the filter component is an absolute div, so z-index does not work :(
  React.useEffect(() => {
    const onClickFilterButton = event => {
      setIsFilterBoxOpen(event.detail.isOpen);
    };

    document.body.addEventListener(
      CUSTOM_EVENTS.FILTER_BUTTON_CLICK,
      onClickFilterButton,
    );

    return () => {
      document.body.removeEventListener(
        CUSTOM_EVENTS.FILTER_BUTTON_CLICK,
        onClickFilterButton,
      );
    };
  }, []);

  React.useEffect(() => {
    const handleScroll = _throttle(() => {
      const scrollElement: HTMLDivElement | null = document.querySelector(
        '#courseListDesktopWrapper',
      );
      const scrollTop = scrollElement
        ? scrollElement.getBoundingClientRect().top
        : 0;

      if (quantityCoursesinPack > 0) {
        setShowpackCourses(true);
      } else {
        setShowpackCourses(false);
      }

      if (scrollTop > 160) {
        setCoursePackPosition(CoursePackPosition.TOP);
      } else {
        setCoursePackPosition(CoursePackPosition.BELOW_FILTERS);
      }
    }, 100);

    if (!matchMediaQuery('md').matches && !isCouponCampaign) {
      setMobile(true);
      setShowpackCourses(true);
    } else {
      window.addEventListener('scroll', handleScroll);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [quantityCoursesinPack]);

  React.useEffect(() => {
    const emptyArray = Array.from(Array(bundleSize));

    if (quantityCoursesinPack > 0) {
      setShowpackCourses(true);
      setCourses([...coursesInBundle, ...emptyArray].slice(0, bundleSize));
    } else {
      setShowpackCourses(false);
      setCourses(emptyArray);
    }
  }, [quantityCoursesinPack, coursesInBundle, bundleSize]);

  if (!hasInitialized) {
    return null;
  }

  return (
    <div
      className="fixed w-screen bg-white"
      css={`
        transition: opacity linear 0.2s;
        bottom: 0;
        z-index: ${isFilterBoxOpen ? 10 : defaultZIndex};
        pointer-events: ${showPackCourses ? 'all' : 'none'};
        opacity: ${showPackCourses ? 1 : 0};
        ${props => props.theme.breakpoints.up('md')} {
          transition: transform 0.2s linear, opacity linear 0.2s;
          left: -5px;
          height: 88px;
          ${!dockToBottom
            ? `
          top: 0;
          bottom: unset;
          transform: translateY(${isCoursePackOnTop ? 0 : 80}px);
          `
            : ``}
        }
      `}
    >
      <Container
        className="bg-white flex flex-col sm:flex-row justify-center items-center mx-auto py-16"
        css={`
          ${props => props.theme.breakpoints.up('md')} {
            ${!dockToBottom
              ? 'box-shadow: rgba(92, 84, 122, 0.08) -1px 15px 18px;'
              : ''}
            ${isCoursePackOnTop &&
              `
              max-width: 100vw !important;
              width: 100vw !important;
            `}
          }
        `}
      >
        <div
          className="hidden md:flex flex-col"
          css={`
            width: auto;
            ${props => props.theme.breakpoints.up('md')} {
              width: 180px;
            }
          `}
        >
          <Text type="body4" color="gray-dark" className="mb-8">
            {t('COURSE_PACK_TITLE', { defaultValue: 'Elige tus cursos' })}
          </Text>
          <Text
            type="link"
            color="secondary-main"
            className="cursor-pointer"
            onClick={() =>
              isCompleted ? cleanCustomBundle() : completeRecommendedCourses()
            }
          >
            {!isCompleted && (
              <span className="mr-4">
                <Wand2 size={14} color={secondaryMain} />
              </span>
            )}
            {isCompleted
              ? t('BANNER_COURSE_PACK_CLEAN', {
                  defaultValue: 'Limpiar selección',
                })
              : t('BANNER_COURSE_PACK_RECOMMENDATION', {
                  defaultValue: 'Recomiéndame cursos',
                })}
          </Text>
        </div>
        <div className="w-full justify-center sm:justify-start sm:w-auto flex md:ml-48">
          {courses.map((course, index) => (
            <CoursePackItem
              key={course ? course.originalId : index}
              courses={courses}
              course={course}
              index={index}
              removeCourseFromPack={toggleCourseInBundle}
            />
          ))}
        </div>

        <div className="flex sm:ml-40 mt-16 sm:mt-0 w-full sm:w-auto">
          <PrimaryButton
            className="w-full sm:w-auto"
            disabled={!isCompleted || isBundleLoading}
            label={
              ctaButtonText ||
              `${t('COURSE_BANNER_PACK_BUTTON', {
                defaultValue: 'Llévatelos por',
              })} ${bundlePrice}`
            }
            onClick={() => {
              mixpanelTracking.track('Buy custom bundle', {}, () => {
                const next = () => {
                  const product = formatProduct(
                    { originalId, name: 'customBundle' },
                    'customBundle',
                  );

                  if (isMobile) {
                    setCheckoutPopupState({
                      open: true,
                      data: { itemId: originalId, itemType: 'custom_bundle' },
                    });
                    trackProduct(product);
                  } else {
                    mixpanelTracking.track(mixpanelTracking.EVENTS.BUY, {
                      originalId,
                      itemType: 'custom_bundle',
                    });

                    buyProduct(redirectUrl, product);
                  }
                };

                if (onClickBuyNow) {
                  onClickBuyNow(redirectUrl, next);
                } else {
                  next();
                }
              });
            }}
          />
        </div>
      </Container>
      {isMobile && checkoutPopupState.open && (
        <Loadable
          Loader={
            <div
              className="fixed inset-0 bg-base-main-80
            flex justify-center items-center"
            >
              <LoaderRing
                color="#fff"
                sizeContainer="50px"
                sizeRing="40px"
                border="3px"
              />
            </div>
          }
          module={() =>
            import(
              /* webpackChunkName: "checkout-dialog"  */ 'Jsx/Shared/CheckoutDialog'
            )
          }
          paymentDialogVisibility={checkoutPopupState.open}
          hidePaymentDialog={() =>
            setCheckoutPopupState(prev => ({
              ...prev,
              open: false,
            }))
          }
          itemData={checkoutPopupState.data}
          paymentSource="CUSTOM_BUNDLE_POP_UP_VIEW"
        />
      )}
    </div>
  );
};

export default CoursesInBundle;
